import React from 'react';
import './bloodwork-display.css';
import { Card, Table, Button, Dropdown } from 'react-bootstrap';
import Moment from 'react-moment';
import { observer, inject } from 'mobx-react';
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  Client,
  FurtherConsiderations,
} from '../../models';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { BloodworkDetailsModal } from './bloodwork-details-modal';
import { BloodworkTestAnalysis } from './bloodwork-test-analysis';
import { UserProfile } from '../../models/UserProfile';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnalytics } from '@fortawesome/pro-solid-svg-icons';
import { faAnalytics as faAnalyticsLight } from '@fortawesome/pro-light-svg-icons';
import { faEye, faTable } from '@fortawesome/pro-regular-svg-icons';
import { GenericModal } from '../common/generic-modal';

//This file is for the Home page dashboard

// A client with many prior tests gives the "Compare with:" menu more entries
// than fit on screen. popper.js v1 sizes the menu before it positions it, so a
// long list flips above the toggle and keeps its full height, running off the
// top of the page. What swallows the oldest dates there is .App-sticky-top: it
// is pinned at z-index 1030 and the menu is 1000, so the header paints over the
// part of the menu level with it rather than the menu covering the header. Cap
// the menu to the room actually left on whichever side popper picked, measured
// from the bottom of the sticky header rather than the top of the window, and
// let the rest scroll.
type PopperData = {
  placement: string;
  instance: { reference: Element };
  styles: { [key: string]: string };
};

// The cap alone is not enough to tell anyone the rest of the list is there:
// macOS draws overlay scrollbars only mid-scroll, so a capped menu looks like a
// list that ends at the oldest date it could fit. .always-show-scrollbar (App.css)
// keeps the bar drawn for as long as the menu overflows. Exported alongside the
// popper config for the same reason it is: the cap, the overflow and the
// scrollbar only behave together, and the test builds its menu from these rather
// than from a second copy of them.
export const COMPARE_MENU_CLASS = 'py-2 always-show-scrollbar';

export const COMPARE_MENU_POPPER_CONFIG = {
  modifiers: {
    preventOverflow: { boundariesElement: 'viewport', padding: 8 },
    fitInViewport: {
      enabled: true,
      // After computeStyle (850) so data.styles exists, before react-overlays'
      // updateStateModifier (900) copies them onto the menu.
      order: 890,
      fn: (data: PopperData) => {
        const toggle = data.instance.reference.getBoundingClientRect();
        const gutter = 16;
        const sticky = document.querySelector(
          '.App-sticky-top',
        ) as HTMLElement | null;
        const ceiling = sticky ? sticky.getBoundingClientRect().bottom : 0;
        const available = data.placement.startsWith('top')
          ? toggle.top - ceiling - gutter
          : window.innerHeight - toggle.bottom - gutter;
        data.styles.maxHeight = `${Math.max(Math.round(available), 160)}px`;
        data.styles.overflowY = 'auto';
        return data;
      },
    },
  },
};

type Props = {
  testResultsPanelLabel: string;
  showPreviousOnCharts?: boolean;
  bloodworks: Bloodwork[];
  bloodworkEntryStore: BloodworkEntryStore;
  currentUserProfile: UserProfile;
  refreshBloodworks: () => Promise<Bloodwork[]>;
  loadClientBloodworks?: (clientId: string) => Promise<Bloodwork[]>;
  furtherConsiderations: FurtherConsiderations;
  allowLoadMoreTestResults?: boolean;
  hideEditTestResultsButton?: boolean;
  history?: any;
  showAnalyzeTableButton?: boolean;
  updateClient?: (client: Client) => Promise<Client>;
  currentClients?: Client[];
};

type State = {
  collectionDate: string;
  bloodworkAnalysis: BloodworkAnalysis;
  /**
   * The test shown in the View Results modal, and ONLY that.
   *
   * It used to be the same field the analysis panel read, so opening the modal
   * on one test while analyzing another silently re-ran the whole analysis
   * behind the modal and scrolled the page down to it. The analysis selection
   * now lives on the store (BloodworkEntryStore.analyzedBloodwork), which
   * separates the two.
   */
  detailsBloodwork: Bloodwork;
  showBloodworkDetailsModal: boolean;
  showUpgradeModal: boolean;
};

@inject('bloodworkEntryStore')
@observer
export class BloodworkDisplay extends React.Component<Props, State> {
  state: State = {
    collectionDate: '',
    bloodworkAnalysis: new BloodworkAnalysis(),
    detailsBloodwork: undefined,
    showBloodworkDetailsModal: false,
    showUpgradeModal: false,
  };

  bloodworkRanges: BloodworkRange[] = [];

  componentDidMount() {
    console.log('BloodworkDisplay.componentDidMount....');
    const { refreshBloodworks, bloodworkEntryStore, allowLoadMoreTestResults } =
      this.props;
    if (allowLoadMoreTestResults) {
      refreshBloodworks().then((bloodworks: Bloodwork[]) => {
        console.log(
          '****************** about to load bloodwork ranges **********',
        );
        return bloodworkEntryStore
          .loadBloodworkRanges()
          .then((bloodworkRanges: BloodworkRange[]) => {
            this.bloodworkRanges = bloodworkRanges;
            console.log('********************* bloodworkRanges **********');
            console.log(this.bloodworkRanges);
            return bloodworks;
          });
      });
    }
  }

  componentWillUnmount() {
    // The open analysis is a selection made on THIS page. Left in the store it
    // would follow the practitioner to the next one and sit there describing a
    // test they are no longer looking at, which is the confusion the store copy
    // exists to end rather than to spread.
    this.props.bloodworkEntryStore.setAnalyzedBloodwork(undefined, undefined);
  }

  analyze = (
    bloodwork: Bloodwork,
    index: number,
    compare?: boolean,
    comparisonBloodwork?: Bloodwork,
  ) => {
    console.log('inside analyze.....');
    console.log(bloodwork);
    const { bloodworks, showPreviousOnCharts, bloodworkEntryStore } = this.props;
    let previousBloodwork: Bloodwork;

    if (showPreviousOnCharts && compare) {
      if (comparisonBloodwork) {
        previousBloodwork = comparisonBloodwork;
      } else if (index + 1 <= bloodworks.length) {
        previousBloodwork = bloodworks[index + 1];
      }
    }

    bloodworkEntryStore.setAnalyzedBloodwork(bloodwork, previousBloodwork);
  };

  loadMoreBloodworks = () => {
    // analyze() picks the comparison test by its index in this list, so the
    // selection cannot outlive a change to what the list contains.
    this.props.bloodworkEntryStore.setAnalyzedBloodwork(undefined, undefined);

    const { bloodworks, bloodworkEntryStore } = this.props;
    const numBloodworks = bloodworks.length;
    const lastCreateDate = bloodworks[numBloodworks - 1].createDate;
    this.toggleLoadMoreResultsButton(true);
    bloodworkEntryStore
      .loadBloodworks(BloodworkEntryStore.DEFAULT_LIMIT, lastCreateDate)
      .then(data => {
        if (!data.LastEvaluatedKey) {
          this.hideLoadMoreResultsButton();
        } else {
          this.toggleLoadMoreResultsButton(false);
        }
      })
      .catch(error => {
        console.error(error);
        this.toggleLoadMoreResultsButton(false);
      });
  };

  hideLoadMoreResultsButton() {
    const element = document.getElementById('loadMoreResultsButton');
    if (element) {
      element.style.display = 'none';
    }
  }

  toggleLoadMoreResultsButton(disable: boolean) {
    const element = document.getElementById('loadMoreResultsButton');
    if (element) {
      if (disable) {
        element.setAttribute('disabled', 'true');
      } else {
        element.removeAttribute('disabled');
      }
    }
  }

  viewResults(bloodwork: Bloodwork) {
    this.setState({
      detailsBloodwork: bloodwork,
      showBloodworkDetailsModal: true,
    });
  }

  onHideBloodworkDetails() {
    this.setState({
      showBloodworkDetailsModal: false,
    });
  }

  editResults(bloodwork: Bloodwork) {
    console.log(bloodwork);
    const { currentUserProfile, bloodworkEntryStore } = this.props;
    if (currentUserProfile.editTestResultsAllowed) {
      // Clear the analyzed bloodwork so the Test Results Analysis panel
      // collapses immediately. Otherwise BloodworkTestAnalysis re-runs
      // analyze() on the prop change and scrollIntoView's back down to the
      // panel — or a pending 1s scroll setTimeout fires after navigation —
      // landing the user on the Analysis section instead of the Edit form.
      bloodworkEntryStore.setAnalyzedBloodwork(undefined, undefined);
      bloodworkEntryStore.setCurrentBloodwork(bloodwork);
      bloodworkEntryStore.setHideEnterTestResults(false);
      if (this.props.history) {
        // When editing from Manage Test Results, remember where to return so
        // saveBloodwork can route back to the same client's results instead
        // of leaving the user on the generic entry page.
        const fromManage =
          this.props.history.location &&
          this.props.history.location.pathname === '/bloodwork/analyze';
        if (fromManage) {
          const clientId =
            bloodwork.clientId || (bloodwork.client && bloodwork.client.id);
          this.props.history.push({
            pathname: '/bloodwork/edit',
            state: { returnTo: '/bloodwork/analyze', clientId },
          });
        } else {
          this.props.history.push('/bloodwork/edit');
        }
      }
    } else {
      this.setState({
        showUpgradeModal: true,
      });
    }
  }

  async deleteBloodwork(bloodwork: Bloodwork) {
    const deleteBloodwork = window.confirm(
      'Are you sure you want to delete the test results?',
    );
    if (deleteBloodwork) {
      const { bloodworkEntryStore, loadClientBloodworks } = this.props;
      await bloodworkEntryStore.deleteBloodwork(bloodwork);
      if (loadClientBloodworks) {
        loadClientBloodworks(bloodwork.client.id);
      }
    }
  }

  render() {
    const {
      testResultsPanelLabel,
      bloodworks,
      currentUserProfile,
      bloodworkEntryStore,
      furtherConsiderations,
      hideEditTestResultsButton,
      showPreviousOnCharts,
      showAnalyzeTableButton,
    } = this.props;
    const { showUpgradeModal, showBloodworkDetailsModal, detailsBloodwork } =
      this.state;
    const { analyzedBloodwork, analyzedPreviousBloodwork } = bloodworkEntryStore;

    if (!bloodworks || !bloodworks.length) {
      return null;
    }

    return (
      <>
        <Card>
          <Card.Header>{testResultsPanelLabel}</Card.Header>
          <Card.Body>
            <div>
              {bloodworks.length > 0 ? (
                <Table
                  striped
                  bordered
                  className="text-nowrap labsmartz-table bloodwork-table"
                >
                  <thead>
                    <tr>
                      <th>Actions</th>
                      <th>Client</th>
                      <th>Sex</th>
                      <th>Collection Date</th>
                      <th>Create Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bloodworks.map((bloodwork: Bloodwork, index: number) => {
                      return (
                        <tr key={index}>
                          <td className="text-nowrap">
                            <Button
                              type="button"
                              title="Analyze Results"
                              style={{ width: 42 }}
                              onClick={() => {
                                this.analyze(bloodwork, index);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faAnalytics}
                                transform={{ x: -1 }}
                              />
                            </Button>
                            &nbsp;&nbsp;
                            {showAnalyzeTableButton ? (
                              <>
                                <Button
                                  type="button"
                                  style={{ width: 42 }}
                                  title="Analyze Results Table"
                                  onClick={() => {
                                    if (this.props.history) {
                                      this.props.history.push({
                                        pathname: '/bloodwork/analyze',
                                        state: {
                                          clientId: bloodwork.client.id,
                                          scrollToComparison: true,
                                        },
                                      });
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faTable}
                                    transform={{ size: 20, y: 1 }}
                                  />
                                </Button>
                                &nbsp;&nbsp;
                              </>
                            ) : null}
                            {showPreviousOnCharts &&
                            currentUserProfile?.showAnalyzeResultsComparison ? (
                              <>
                                <Dropdown className="d-inline-block">
                                  <Dropdown.Toggle
                                    variant="primary"
                                    title="Analyze Results Comparison"
                                    id={`analyze-split-button-${index}`}
                                    disabled={index === bloodworks.length - 1}
                                  >
                                    <FontAwesomeIcon icon={faAnalyticsLight} />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu
                                    className={COMPARE_MENU_CLASS}
                                    style={{
                                      maxHeight: '50vh',
                                      overflowY: 'auto',
                                    }}
                                    popperConfig={COMPARE_MENU_POPPER_CONFIG}
                                  >
                                    <Dropdown.Header className="px-3 py-1">
                                      Compare with:
                                    </Dropdown.Header>
                                    {bloodworks
                                      .slice(index + 1)
                                      .map((olderBloodwork: Bloodwork) => (
                                        <Dropdown.Item
                                          key={olderBloodwork.id}
                                          className="px-3 py-1"
                                          onClick={() => {
                                            // Blur the focused item before the
                                            // menu closes. Otherwise react-overlays
                                            // restores focus to the dropdown toggle
                                            // on close (toggleElement.focus() in a
                                            // useEffect), and that focus() scrolls
                                            // the toggle — at the top of this panel —
                                            // back into view AFTER our
                                            // scrollToAnalyzeTestResults() runs,
                                            // yanking the page back up instead of
                                            // landing on Test Results Analysis. With
                                            // focus removed, focusInDropdown is false
                                            // on close so the toggle isn't refocused.
                                            (document.activeElement as HTMLElement | null)?.blur();
                                            this.analyze(
                                              bloodwork,
                                              index,
                                              true,
                                              olderBloodwork,
                                            );
                                          }}
                                        >
                                          <Moment format="MM/DD/YYYY">
                                            {olderBloodwork.collectionDate}
                                          </Moment>
                                        </Dropdown.Item>
                                      ))}
                                  </Dropdown.Menu>
                                </Dropdown>
                                &nbsp;&nbsp;
                              </>
                            ) : null}
                            <Button
                              type="button"
                              style={{ width: 42 }}
                              title="View Results"
                              onClick={() => {
                                this.viewResults(bloodwork);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faEye}
                                transform={{ size: 18, y: 1, x: -1 }}
                              />
                            </Button>
                            &nbsp;&nbsp;
                            {!hideEditTestResultsButton ? (
                              <>
                                <Button
                                  type="button"
                                  style={{ width: 42 }}
                                  title="Edit Results"
                                  onClick={() => {
                                    this.editResults(bloodwork);
                                  }}
                                >
                                  <FontAwesomeIcon icon="edit" />
                                </Button>
                                &nbsp;&nbsp;
                              </>
                            ) : null}
                            <Button
                              type="button"
                              style={{ width: 42 }}
                              title="Delete Results"
                              onClick={() => {
                                this.deleteBloodwork(bloodwork);
                              }}
                            >
                              <FontAwesomeIcon icon="trash-alt" />
                            </Button>
                          </td>
                          <td className="text-nowrap">
                            {bloodwork.client
                              ? bloodwork.client.properName
                              : ''}
                          </td>
                          <td className="text-nowrap">
                            {bloodwork.client
                              ? bloodwork.client.displayGender
                              : ''}
                          </td>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {bloodwork.collectionDate}
                            </Moment>
                          </td>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {bloodwork.createDate}
                            </Moment>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <div />
              )}
            </div>
            {this.props.allowLoadMoreTestResults ? (
              <div id="load-results-button-container">
                <Button
                  id="loadMoreResultsButton"
                  onClick={() => {
                    this.loadMoreBloodworks();
                  }}
                >
                  Load More Results
                </Button>
              </div>
            ) : null}
          </Card.Body>
        </Card>

        <BloodworkTestAnalysis
          currentUserProfile={currentUserProfile}
          bloodwork={analyzedBloodwork}
          previousBloodwork={analyzedPreviousBloodwork}
          bloodworkRanges={bloodworkEntryStore.effectiveRanges}
          furtherConsiderations={furtherConsiderations}
          bloodworkEntryStore={bloodworkEntryStore}
          updateClient={this.props.updateClient}
          currentClients={this.props.currentClients}
        />

        <BloodworkDetailsModal
          show={showBloodworkDetailsModal}
          bloodwork={detailsBloodwork}
          bloodworkRanges={bloodworkEntryStore.effectiveRanges}
          onHide={() => {
            this.onHideBloodworkDetails();
          }}
        />

        <GenericModal
          show={showUpgradeModal}
          title="Edit Test Results Feature Not Available"
          text="Edit Test Results feature is not available with a free account."
          link="https://labsmarts.com/pricing"
          linkText="Click here to upgrade."
          onHide={() => {
            this.setState({ showUpgradeModal: false });
          }}
        />
      </>
    );
  }
}
